import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { LDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { launchDarklyClientId } from 'constants/config';
import type { FeatureFlags } from 'constants/feature-flags';
import { localFeatureFlagValues, readFeatureFlagOverrides } from 'constants/feature-flags';
import { FeatureFlagsContext } from 'contexts/feature-flags-context';
import useStores from 'hooks/use-stores.hooks';
import { buildLaunchDarklyContext } from 'utils/feature-flags.utils';

const MAX_IDENTIFY_RETRIES = 3;
const IDENTIFY_RETRY_MS = 5000;

const useLaunchDarklyContext = () => {
  // Every spec renders inside this provider, and some of them mock `useStores` down to the stores
  // they assert on, so neither store is guaranteed to be there.
  const { userStore, organizationsStore } = useStores() ?? {};

  const profile = userStore?.profile;
  const organization = organizationsStore?.currentOrganization;
  // Wait for the session before evaluating anything: the client initializes on the first context it
  // is given, and a context built before the profile and the organizations landed would evaluate
  // every flag for someone we do not know yet.
  const hasResolvedSession = !!profile && !!organizationsStore?.isDoneFetchingOrganizations;
  // The organizations map is refetched on navigation, so the selected organization is briefly absent.
  // Identifying then would drop the organization out of the context and re-evaluate every flag without it.
  const isOrganizationPending = !!organizationsStore?.currentOrganizationId && !organization;

  const context = useMemo(
    () => (hasResolvedSession ? buildLaunchDarklyContext({ profile: profile!, organization }) : undefined),
    [hasResolvedSession, profile, organization]
  );

  return { context, isOrganizationPending };
};

const FeatureFlagsBridge: React.FunctionComponent<{
  overrides: Partial<FeatureFlags>;
  children: React.ReactNode;
}> = observer(function FeatureFlagsBridge({ overrides, children }) {
  const ldClient = useLDClient();
  const ldFlags = useFlags();
  const { context, isOrganizationPending } = useLaunchDarklyContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const [retry, setRetry] = useState(0);
  const identifiedContext = useRef<string | null>(null);
  const hasEvaluatedInitialContext = useRef(false);

  // Each identify re-opens the streaming connection, so skip the ones that change nothing.
  useEffect(() => {
    if (!ldClient || !context || isOrganizationPending) return;

    const serialized = JSON.stringify(context);
    if (serialized === identifiedContext.current) return;

    // The client exists because `deferInitialization` let it initialize on this very context, so the
    // first one it sees is already identified.
    if (!hasEvaluatedInitialContext.current) {
      hasEvaluatedInitialContext.current = true;
      identifiedContext.current = serialized;
      return;
    }

    identifiedContext.current = serialized;
    let timer: ReturnType<typeof setTimeout>;

    ldClient.identify(context).catch(error => {
      console.warn('Failed to identify LaunchDarkly context', error);
      // Serving flags evaluated for whoever was identified before is worse than trying again, so
      // forget the attempt and schedule the retry. Bounded, because a permanent failure would
      // otherwise reconnect forever.
      if (identifiedContext.current === serialized) identifiedContext.current = null;
      if (retry < MAX_IDENTIFY_RETRIES) timer = setTimeout(() => setRetry(attempt => attempt + 1), IDENTIFY_RETRY_MS);
    });

    return () => clearTimeout(timer);
  }, [ldClient, context, isOrganizationPending, retry]);

  useEffect(() => {
    if (!ldClient) return;

    let isStale = false;
    ldClient.waitUntilReady().then(() => !isStale && setIsInitialized(true));
    return () => {
      isStale = true;
    };
  }, [ldClient]);

  const value = useMemo(
    () => ({
      featureFlags: { ...localFeatureFlagValues, ...ldFlags, ...overrides },
      isInitialized
    }),
    [ldFlags, overrides, isInitialized]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
});

const FeatureFlagsProvider: React.FunctionComponent<{ clientSideId?: string; children: React.ReactNode }> = ({
  clientSideId = launchDarklyClientId,
  children
}) => {
  const overrides = useMemo(readFeatureFlagOverrides, []);
  const { context } = useLaunchDarklyContext();

  const offlineValue = useMemo(
    () => ({ featureFlags: { ...localFeatureFlagValues, ...overrides }, isInitialized: true }),
    [overrides]
  );

  if (!clientSideId) {
    return <FeatureFlagsContext.Provider value={offlineValue}>{children}</FeatureFlagsContext.Provider>;
  }

  return (
    <LDProvider
      clientSideID={clientSideId}
      context={context}
      deferInitialization
      options={{ bootstrap: 'localStorage' }}
      reactOptions={{ useCamelCaseFlagKeys: false }}>
      <FeatureFlagsBridge overrides={overrides}>{children}</FeatureFlagsBridge>
    </LDProvider>
  );
};

export default observer(FeatureFlagsProvider);
